import React, { useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import moment from "moment";

import {
  ITransferInfoLineProps,
  OwnershipTransferDialog,
} from "components/OwnershipTransferDialog";
import { Menu, MenuItem } from "components/shared";
import { CUSTOM_PANELS } from "helpers/apis";
import useAxios from "helpers/axios";
import useAuth from "hooks/useAuth";
import { ICustomPanel } from "types/panel";
import { IUser, OrgUser, UserRole } from "types/user";

import useDraftCustomPanel from "../../useDraftCustomPanel";
import { DeleteConfirmDialog } from "../DeleteConfirmDialog";

interface IActionsMenuProps {
  anchorEl: HTMLElement | null;
  selectedDraft: ICustomPanel;
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
}

export const ActionsMenu: React.FC<IActionsMenuProps> = ({
  anchorEl,
  selectedDraft,
  setAnchorEl,
}) => {
  const { hasPermission, user } = useAuth();
  const history = useHistory();
  const { pathname } = useLocation();
  const { setDraftCustomPanels, setMessage } = useDraftCustomPanel();
  const isAdmin = hasPermission([UserRole.Admin]);
  const isSalesRep = hasPermission([UserRole.SalesRep]);
  const [deleteConfirmDlg, setDeleteConfirmDlg] = useState<boolean>(false);
  const [openTransferDialog, setOpenTransferDialog] = useState<boolean>(false);

  const infoLines: ITransferInfoLineProps[] = useMemo(
    () => [
      {
        label: "Date Saved",
        infoText: moment(selectedDraft.modifiedAt).format("YYYY-MM-DD"),
      },
      {
        label: "Panel Name",
        infoText: selectedDraft.name,
      },
      {
        label: "Current Owner",
        infoText: (
          <>
            {selectedDraft.owner?.firstName} {selectedDraft.owner?.lastName}
            <br />
            {selectedDraft.owner?.email}
          </>
        ),
      },
    ],
    [selectedDraft]
  );

  const { fetchData } = useAxios({
    config: {
      url: `${CUSTOM_PANELS}${selectedDraft.id}`,
      method: "PUT",
    },
    callOnInit: false,
  });

  const canTransferOwnership = useMemo(() => {
    if (isAdmin) {
      return true;
    }
    if (isSalesRep && selectedDraft.owner.id === user?.id) {
      return true;
    }
    return false;
  }, [selectedDraft, isAdmin, isSalesRep, user]);

  const onView = () => {
    history.push(`${pathname}/${selectedDraft.id}/view`);
  };
  const onEdit = () => {
    history.push(`${pathname}/${selectedDraft.id}/edit`);
  };
  const transferPanelOwnership = (
    newOwner: OrgUser,
    setError: (error: string) => void
  ) => {
    fetchData(
      {
        data: {
          owner: newOwner?.id,
          status: "draft",
        },
      },
      () => {
        setMessage("ownership_transferred");
        setOpenTransferDialog(false);
        if (isAdmin) {
          setDraftCustomPanels((draftCustomPanels) => [
            ...draftCustomPanels.map((item) => {
              if (item.id === selectedDraft.id) {
                return {
                  ...item,
                  owner: newOwner as any as IUser,
                };
              }
              return item;
            }),
          ]);
        } else {
          setDraftCustomPanels((draftCustomPanels) => [
            ...draftCustomPanels.filter((item) => item.id !== selectedDraft.id),
          ]);
        }
      },
      ({ error }: { error: string }) => {
        setError(error);
      }
    );
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        onClick={() => setAnchorEl(null)}
        paperWidth={160}
      >
        <MenuItem size="sm" onClick={onView}>
          View
        </MenuItem>
        <MenuItem size="sm" onClick={onEdit}>
          Edit
        </MenuItem>
        <MenuItem size="sm" onClick={() => setDeleteConfirmDlg(true)}>
          Delete
        </MenuItem>
        {canTransferOwnership && (
          <MenuItem size="sm" onClick={() => setOpenTransferDialog(true)}>
            Transfer Ownership
          </MenuItem>
        )}
      </Menu>

      {deleteConfirmDlg && (
        <DeleteConfirmDialog
          open={deleteConfirmDlg}
          setOpen={setDeleteConfirmDlg}
          customPanel={selectedDraft}
        />
      )}
      {canTransferOwnership && openTransferDialog && (
        <OwnershipTransferDialog
          infos={infoLines}
          onTransfer={transferPanelOwnership}
          openDialog
          setOpenDialog={setOpenTransferDialog}
        />
      )}
    </>
  );
};
